import { useCallback, useEffect, useRef, useState } from 'react';
import { DocumentNode, useApolloClient } from '@apollo/client';
import { useToaster } from '@/components/core/Toaster';
import { BackfillRunStatus, RunStatusQueryData, RunStatusQueryVars } from '@/graph/run-status';

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 60; // ~3 minutes at 3s intervals
const MAX_NULL_ATTEMPTS = 5; // give up if no run record appears after ~15s

export interface UseBackfillRunStatusPollingResult {
  /** Latest polled status, or null before the first successful poll. */
  status: BackfillRunStatus | null;
  /** The runId currently being polled, or null when idle. */
  runId: string | null;
  /** True while a poll loop is active (drives button disabled state). */
  isPolling: boolean;
  /** Begin polling the given runId; replaces any in-flight poll loop. */
  start: (runId: string) => void;
  /** Clear status and stop polling (call before re-invoking). */
  reset: () => void;
}

/**
 * Polls a run-status query until the run and all its enqueued worker jobs
 * finish, surfacing the terminal outcome via the toaster. Shared by the
 * executive-analytics and staff-attribution admin dialogs.
 *
 * @param query A run-status query that aliases its field to `runStatus`
 *   (see `RUN_STATUS_FIELDS` in `@/graph/run-status`).
 * @param label Human label for toasts (e.g. "Coordinator", "Backfill").
 */
export function useBackfillRunStatusPolling(query: DocumentNode, label: string): UseBackfillRunStatusPollingResult {
  const client = useApolloClient();
  const toaster = useToaster();

  const [status, setStatus] = useState<BackfillRunStatus | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stop = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Stop any pending timer on unmount.
  useEffect(() => stop, [stop]);

  const reset = useCallback(() => {
    stop();
    setStatus(null);
    setRunId(null);
    setIsPolling(false);
  }, [stop]);

  const start = useCallback(
    (nextRunId: string) => {
      stop();
      setRunId(nextRunId);
      setStatus(null);
      setIsPolling(true);

      let attempts = 0;
      let nullCount = 0;

      const finish = () => {
        pollRef.current = null;
        setIsPolling(false);
      };

      const poll = async () => {
        attempts++;
        try {
          const { data } = await client.query<RunStatusQueryData, RunStatusQueryVars>({
            query,
            variables: { runId: nextRunId },
            fetchPolicy: 'network-only',
          });
          const next = data?.runStatus ?? null;
          if (next) {
            setStatus(next);
            // Done when the run itself finished AND all worker jobs completed.
            const allJobsDone =
              next.enqueuedJobs > 0 ? next.completedJobs >= next.enqueuedJobs : next.status !== 'running';
            if (next.status !== 'running' && allJobsDone) {
              finish();
              // The run-level status only reflects fan-out success (workers run
              // async), so worker failures surface only via `failedJobs` — a
              // non-zero count is a failure even when the run reports success.
              const failed = next.failedJobs > 0 || next.enqueueFailures > 0 || next.status !== 'success';
              if (failed) {
                const ok = next.completedJobs - next.failedJobs;
                const parts = [`${ok}/${next.enqueuedJobs} ok`];
                if (next.failedJobs > 0) parts.push(`${next.failedJobs} failed`);
                if (next.enqueueFailures > 0) parts.push(`${next.enqueueFailures} not enqueued`);
                toaster.error(`${label} finished with failures: ${parts.join(', ')}`);
              } else {
                toaster.success(`${label} complete: ${next.completedJobs}/${next.enqueuedJobs} jobs`);
              }
              return;
            }
            if (attempts >= MAX_ATTEMPTS) {
              finish();
              toaster.error(
                `${label} polling timed out: status=${next.status}, ${next.completedJobs}/${next.enqueuedJobs} jobs`
              );
              return;
            }
          } else {
            nullCount++;
            if (nullCount >= MAX_NULL_ATTEMPTS) {
              finish();
              toaster.error(
                `${label} failed: no run record found. The Lambda may have crashed before starting — check CloudWatch logs.`
              );
              return;
            }
          }
        } catch (err) {
          console.error(`${label} status poll error:`, err);
          if (attempts >= MAX_ATTEMPTS) {
            finish();
            toaster.error(`${label} status polling failed`);
            return;
          }
        }
        // Schedule next poll only after the current one resolves.
        pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      };

      poll();
    },
    [client, toaster, query, label, stop]
  );

  return { status, runId, isPolling, start, reset };
}
